import { useNavigate } from "react-router";
import { useCompanies } from "../lib/companies";
import { useRunNow } from "../lib/runs";
import { Button } from "../components/ui/Button";
import { Pill, type PillTone } from "../components/ui/Pill";
import { EmptyState } from "../components/ui/EmptyState";
import {
  TableShell,
  Table as UITable,
  THead,
  TRow,
  TCell,
} from "../components/ui/Table";
import { Skeleton, SkeletonRow } from "../components/ui/Skeleton";

export default function Companies() {
  const q = useCompanies();

  return (
    <section className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
      <header className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Companies</h1>
        <span className="text-xs text-neutral-500" aria-live="polite">
          {q.data ? (
            `${q.data.length} tracked`
          ) : q.isLoading ? (
            <Skeleton className="inline-block h-3 w-20" />
          ) : null}
        </span>
      </header>
      <p className="mt-2 text-sm text-neutral-400">
        Catalog of companies monitored for senior pre-sales roles. &quot;Run
        now&quot; enqueues a single-company check; you&apos;re rate-limited to
        one trigger per company per 5 minutes.
      </p>

      <div className="mt-6">
        {q.isLoading ? (
          <TableShell>
            <UITable>
              <THead>
                <HeaderRow />
              </THead>
              <tbody>
                {Array.from({ length: 6 }).map((_, i) => (
                  <SkeletonRow key={i} cols={5} />
                ))}
              </tbody>
            </UITable>
          </TableShell>
        ) : q.error ? (
          <TableShell>
            <EmptyState
              tone="danger"
              title="Couldn't load companies"
              description={String(q.error)}
              action={
                <Button variant="secondary" onClick={() => q.refetch()}>
                  Retry
                </Button>
              }
            />
          </TableShell>
        ) : !q.data || q.data.length === 0 ? (
          <TableShell>
            <EmptyState title="Catalog is empty. Run `pnpm seed:companies --apply` to populate." />
          </TableShell>
        ) : (
          <TableShell>
            <UITable>
              <THead>
                <HeaderRow />
              </THead>
              <tbody>
                {q.data.map((c) => (
                  <TRow key={c.id} className="hover:bg-neutral-900/60">
                    <TCell className="font-medium text-neutral-100">
                      <span className="inline-flex items-center gap-2">
                        <span>{c.name}</span>
                        {c.priority ? (
                          <Pill tone="warning" className="uppercase">
                            {c.priority}
                          </Pill>
                        ) : null}
                      </span>
                    </TCell>
                    <TCell>
                      <TierPill tier={c.tier} />
                    </TCell>
                    <TCell className="font-mono text-xs text-neutral-400">
                      {c.ats}
                    </TCell>
                    <TCell>
                      {c.careers_url ? (
                        <a
                          href={c.careers_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-neutral-300 hover:text-neutral-50 underline-offset-4 hover:underline"
                        >
                          Visit
                        </a>
                      ) : (
                        <span className="text-neutral-600">—</span>
                      )}
                    </TCell>
                    <TCell className="text-right">
                      <RunNowButton companyId={c.id} />
                    </TCell>
                  </TRow>
                ))}
              </tbody>
            </UITable>
          </TableShell>
        )}
      </div>
    </section>
  );
}

function HeaderRow() {
  return (
    <tr>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        Name
      </th>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        Tier
      </th>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        ATS
      </th>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        Careers
      </th>
      <th scope="col" className="px-4 py-3 text-right font-medium">
        Actions
      </th>
    </tr>
  );
}

function RunNowButton({ companyId }: { companyId: string }) {
  const navigate = useNavigate();
  const m = useRunNow();

  if (m.isError) {
    const err = m.error;
    if (err.status === 429) {
      const seconds = err.retry_after_ms
        ? Math.ceil(err.retry_after_ms / 1000)
        : err.throttle_seconds ?? 0;
      return (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => m.reset()}
          title={`Rate-limited; try again in ~${seconds}s`}
          className="border-warning-border bg-warning-bg text-warning-fg"
        >
          Wait ~{seconds}s
        </Button>
      );
    }
    return (
      <Button
        variant="danger"
        size="sm"
        onClick={() => m.reset()}
        title={err.error}
      >
        Failed — retry
      </Button>
    );
  }

  return (
    <Button
      variant="secondary"
      size="sm"
      loading={m.isPending}
      onClick={() =>
        m.mutate(companyId, {
          onSuccess: () => navigate("/runs"),
        })
      }
    >
      Run now
    </Button>
  );
}

const TIER_TONE: Record<1 | 2 | 3, PillTone> = {
  1: "success",
  2: "info",
  3: "neutral",
};

function TierPill({ tier }: { tier: 1 | 2 | 3 }) {
  return <Pill tone={TIER_TONE[tier]}>Tier {tier}</Pill>;
}
